import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card } from '../../shared/ui';
import { usePortal } from '../context';
import { UrgencyLegend } from '../components/status';

/**
 * Manual de uso para el personal del cliente (compras, recepción y el
 * administrador del portal), paso a paso y sin jerga. Las tolerancias y el
 * nombre del cliente salen de su configuración, así que el manual dice lo
 * mismo que el portal hace.
 */

const SECTIONS: Array<{ id: string; title: string }> = [
  { id: 'que-es', title: 'Qué es el portal' },
  { id: 'entrar', title: 'Entrar al portal' },
  { id: 'recorrido', title: 'El recorrido de un pedido' },
  { id: 'cartera', title: 'La cartera de pedidos' },
  { id: 'pedido', title: 'Dentro de un pedido' },
  { id: 'validar', title: 'Validar fechas y precios' },
  { id: 'recepcion', title: 'Recepción en playa y luz verde' },
  { id: 'rechazos', title: 'Pedidos rechazados y modificados' },
  { id: 'proveedores', title: 'Dar de alta proveedores y usuarios' },
  { id: 'evaluacion', title: 'Evaluación de proveedores' },
  { id: 'avisos', title: 'Avisos por correo' },
  { id: 'configuracion', title: 'Configuración (administrador)' },
  { id: 'dudas', title: 'Preguntas frecuentes' },
];

function go(id: string): void {
  // El portal usa rutas con «#», así que no valen los anclajes normales.
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Section({ id, n, title, children }: { id: string; n: number; title: string; children: ReactNode }): JSX.Element {
  return (
    <section id={id} className="pt-manual-section">
      <Card title={`${n}. ${title}`}>
        <div className="pt-manual-body">{children}</div>
      </Card>
    </section>
  );
}

export function Manual(): JSX.Element {
  const { me } = usePortal();
  const client = me.tenant.name;
  const { priceTolerancePct: pct, dateToleranceDays: days } = me.rules;

  return (
    <div className="stack lg pt-manual">
      <div className="page-head">
        <div>
          <h1>Manual de uso</h1>
          <p className="muted">
            Cómo trabajar con el portal de proveedores de {client}, paso a paso. Pensado para compras y recepción.
          </p>
        </div>
        <button className="btn sm" onClick={() => window.print()}>
          Imprimir
        </button>
      </div>

      <nav className="card pt-manual-toc" aria-label="Índice del manual">
        <div className="card-body">
          <ol>
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <button type="button" className="pt-manual-link" onClick={() => go(s.id)}>
                  {s.title}
                </button>
              </li>
            ))}
          </ol>
        </div>
      </nav>

      <Section id="que-es" n={1} title="Qué es el portal">
        <p>
          Es la web donde {client} y sus proveedores siguen juntos cada pedido de compra. El proveedor acepta el pedido, da sus fechas y
          comunica el albarán; compras solo tiene que intervenir cuando algo se sale de lo acordado.
        </p>
        <div className="callout info">
          <div className="title">SAP sigue mandando</div>
          Los pedidos se crean y se modifican en SAP como siempre. El portal los lee de SAP cada 5 minutos y graba en SAP lo que pasa en el
          portal (aceptación, fechas, precios, recepción). No hay que dar de alta nada dos veces.
        </div>
        <table className="table pt-manual-table">
          <thead>
            <tr>
              <th>Perfil</th>
              <th>Qué hace en el portal</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Técnico de compras</td>
              <td>Ve la cartera de todos los proveedores, da de alta proveedores y sus usuarios, valida fechas y precios fuera de tolerancia y consulta la evaluación.</td>
            </tr>
            <tr>
              <td>Verificador de recepción</td>
              <td>Confirma en el portal que la mercancía ha llegado físicamente a la playa.</td>
            </tr>
            <tr>
              <td>Administrador del cliente</td>
              <td>Todo lo de compras, más la configuración del portal y los usuarios del personal.</td>
            </tr>
            <tr>
              <td>Proveedor</td>
              <td>Ve solo sus pedidos: los acepta o rechaza, propone fechas, comunica el albarán con precios y descarga los planos.</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section id="entrar" n={2} title="Entrar al portal">
        <ol className="pt-manual-steps">
          <li>
            Abre <strong>{window.location.host}</strong> en el navegador (vale el ordenador, la tablet o el móvil).
          </li>
          <li>
            La primera vez no tienes contraseña: te llega un correo de <em>noreply@ecinsa.com</em> con el asunto «Tu acceso al portal de
            proveedores de {client}». Pulsa el botón del correo, escribe tu contraseña dos veces (mínimo 8 caracteres) y pulsa{' '}
            <strong>Crear mi contraseña y entrar</strong>. El enlace vale 7 días.
          </li>
          <li>
            A partir de ahí entras con tu correo y tu contraseña, y pulsas <strong>Entrar</strong>.
          </li>
          <li>
            Si la olvidas, pulsa <strong>¿Has olvidado tu contraseña?</strong> en la pantalla de entrada, escribe tu correo y te llegará un
            enlace para elegir otra. Ese enlace vale 1 hora.
          </li>
        </ol>
        <p className="subtle">
          Si el correo no llega, mira en la carpeta de correo no deseado. Si tampoco está, pide al administrador del portal que te reenvíe la
          invitación desde Usuarios.
        </p>
      </Section>

      <Section id="recorrido" n={3} title="El recorrido de un pedido">
        <p>
          Este es el camino normal de un pedido. Las casillas resaltadas son los únicos momentos en que compras o recepción tienen que hacer
          algo; el resto lo hace el proveedor o el propio portal.
        </p>
        <OrderFlow pct={pct} days={days} />
        <ol className="pt-manual-steps">
          <li>
            <strong>Compras crea el pedido en SAP</strong>, como siempre, a un proveedor que esté dado de alta en el portal.
          </li>
          <li>
            <strong>El portal lo publica</strong> en el siguiente repaso de SAP (cada 5 minutos) y avisa al proveedor por correo. El pedido
            queda <Badge tone="warn">Pendiente de aceptar</Badge>.
          </li>
          <li>
            <strong>El proveedor lo acepta o lo rechaza.</strong> Si no responde, el portal le manda recordatorios a los 3 y 5 días. Si lo
            rechaza, tiene que decir por qué y compras recibe un aviso.
          </li>
          <li>
            <strong>El proveedor propone una fecha de entrega para cada línea.</strong> Si está a ± {days} días de la fecha requerida, se
            graba sola en SAP como fecha comprometida. Si no, queda <Badge tone="warn">Pendiente de validar</Badge> y compras la aprueba o la
            rechaza.
          </li>
          <li>
            <strong>Cuando envía la mercancía, el proveedor comunica el albarán</strong> con el precio de cada línea (y si quiere, el PDF). Si
            el precio está a ± {pct} % del precio del pedido, queda <Badge tone="ok">Precio conforme</Badge>. Si no, compras lo valida.
          </li>
          <li>
            <strong>Recepción confirma la llegada física</strong> de la mercancía en la playa.
          </li>
          <li>
            Con el precio validado <em>y</em> la mercancía recibida, la línea tiene <Badge tone="ok">Luz verde</Badge>: se puede hacer la
            entrada de mercancía en SAP.
          </li>
        </ol>
      </Section>

      <Section id="cartera" n={4} title="La cartera de pedidos">
        <p>
          Es la pantalla de inicio (<Link to="/">Cartera</Link>). Muestra todas las líneas abiertas de los pedidos de los proveedores del
          portal, una fila por línea, ordenadas de más a menos urgente.
        </p>
        <h3>Los colores</h3>
        <p>Cada línea lleva un color según su fecha comprometida (o, si no la hay, la de entrega de SAP):</p>
        <UrgencyLegend />
        <ul>
          <li>
            <strong>Retrasado</strong> (rojo): la fecha ya ha pasado y la línea sigue abierta.
          </li>
          <li>
            <strong>Próximos 15 días</strong> (ámbar): vence en las dos próximas semanas.
          </li>
          <li>
            <strong>Resto</strong> (verde): vence más adelante.
          </li>
        </ul>
        <h3>Los recuadros de arriba</h3>
        <p>
          Cuentan lo que necesita atención: <strong>Pendientes de aceptar</strong>, <strong>Retrasadas</strong>,{' '}
          <strong>Próximos 15 días</strong>, <strong>Pendientes de recibir</strong> y <strong>Pendientes de validar</strong>. Al pulsar uno,
          la tabla se filtra; vuelve a pulsarlo para quitar el filtro.
        </p>
        <div className="callout warn">
          <div className="title">Lo primero cada día: «Pendientes de validar»</div>
          Son las fechas y precios que el proveedor ha propuesto fuera de tolerancia y que esperan tu respuesta.
        </div>
        <h3>Buscar y filtrar</h3>
        <ul>
          <li>El buscador encuentra por número de pedido, artículo, descripción o proveedor.</li>
          <li>Los desplegables filtran por estado de aceptación, por plazo (abiertas, servidas, todas) y por proveedor.</li>
          <li>
            Un pedido con la etiqueta <Badge tone="info">Modificado</Badge> ha cambiado en SAP después de publicarse.
          </li>
        </ul>
        <h3>Recargar y Repasar SAP ahora</h3>
        <ul>
          <li>
            <strong>Recargar</strong> vuelve a leer los pedidos de SAP para ver los datos al minuto.
          </li>
          <li>
            <strong>Repasar SAP ahora</strong> hace en el momento lo que el portal hace solo cada 5 minutos: publica los pedidos nuevos,
            detecta los modificados y envía los avisos pendientes. Úsalo si acabas de crear un pedido en SAP y no quieres esperar.
          </li>
        </ul>
      </Section>

      <Section id="pedido" n={5} title="Dentro de un pedido">
        <p>Pulsa cualquier línea de la cartera para abrir su pedido. Verás:</p>
        <ul>
          <li>
            <strong>Datos del pedido</strong>: proveedor, fecha del pedido, cuándo se envió al portal, cuándo se aceptó (o el motivo del
            rechazo) y las tolerancias que se aplican.
          </li>
          <li>
            <strong>Un aviso de color</strong> que dice en qué punto está y si te toca hacer algo (por ejemplo, «Pendiente de que el
            proveedor lo acepte»).
          </li>
          <li>
            <strong>Las líneas</strong>, con la cantidad, la fecha requerida, la propuesta por el proveedor, la comprometida, el precio, el
            albarán, la recepción física y la luz verde.
          </li>
          <li>
            <strong>El plano</strong> de cada línea: su versión (v3, por ejemplo) y un ojo que descarga un ZIP con el PDF, el DWG y el STEP de
            esa versión. El proveedor lo puede descargar antes de aceptar.
          </li>
          <li>
            <strong>El historial</strong>: quién hizo qué y cuándo (aceptaciones, fechas, albaranes, validaciones, recepción).
          </li>
        </ul>
      </Section>

      <Section id="validar" n={6} title="Validar fechas y precios">
        <p>
          Solo llegan a compras las fechas y precios que se salen de la tolerancia (± {days} días en fechas y ± {pct} % en precios). Lo que
          está dentro se graba solo.
        </p>
        <ol className="pt-manual-steps">
          <li>
            Te llega un correo («Fecha a validar» o «Precio a validar») y la línea aparece en el recuadro{' '}
            <strong>Pendientes de validar</strong> de la cartera.
          </li>
          <li>Abre el pedido. La línea muestra la fecha o el precio con la etiqueta «Pendiente de validar».</li>
          <li>
            Pulsa <strong>Aprobar fecha</strong> o <strong>Rechazar fecha</strong> (o <strong>Aprobar precio</strong> /{' '}
            <strong>Rechazar precio</strong>) en esa línea. La ventana te enseña lo que pedías y lo que propone el proveedor; puedes dejarle
            un comentario.
          </li>
        </ol>
        <table className="table pt-manual-table">
          <thead>
            <tr>
              <th />
              <th>Si apruebas</th>
              <th>Si rechazas</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Fecha</strong>
              </td>
              <td>Se graba en SAP como fecha comprometida y de entrega de la línea.</td>
              <td>El proveedor la ve rechazada y puede proponer otra.</td>
            </tr>
            <tr>
              <td>
                <strong>Precio</strong>
              </td>
              <td>Se graba en SAP como nuevo precio de la línea.</td>
              <td>El proveedor lo ve rechazado y puede comunicar otro albarán.</td>
            </tr>
          </tbody>
        </table>
        <p className="subtle">
          En los dos casos se avisa al proveedor por correo. El precio se compara siempre con el precio que tenía la línea cuando se publicó
          el pedido, no con el último.
        </p>
      </Section>

      <Section id="recepcion" n={7} title="Recepción en playa y luz verde">
        <p>
          Quien tiene perfil de recepción entra directamente en <strong>Recepción en playa</strong>: la lista de líneas de pedidos aceptados
          que todavía no se han recibido.
        </p>
        <ol className="pt-manual-steps">
          <li>Busca el pedido (por número, artículo o proveedor) y ábrelo.</li>
          <li>Marca las líneas que han llegado (la casilla de arriba marca todas las abiertas).</li>
          <li>
            Pulsa <strong>Confirmar recepción física en playa</strong>. La línea pasa a «Recibida» con la fecha de hoy.
          </li>
          <li>
            Si te equivocas, marca la línea y pulsa <strong>Deshacer recepción</strong>.
          </li>
        </ol>
        <div className="callout ok">
          <div className="title">Luz verde = precio validado + mercancía recibida</div>
          Hacen falta las dos cosas; ninguna sustituye a la otra. Cuando una línea tiene luz verde, se puede hacer la entrada de mercancía en
          SAP con tranquilidad.
        </div>
      </Section>

      <Section id="rechazos" n={8} title="Pedidos rechazados y modificados">
        <h3>Si el proveedor rechaza un pedido</h3>
        <p>
          El proveedor tiene que escribir el motivo (por ejemplo, «sin capacidad de máquina hasta final de mes»). Compras recibe un correo
          con ese motivo y el pedido queda <Badge tone="bad">Rechazado</Badge>. Tienes dos opciones:
        </p>
        <ul>
          <li>Modificarlo en SAP (fecha, cantidad, precio…): el portal se lo vuelve a enviar para que lo acepte.</li>
          <li>Gestionarlo con otro proveedor, como harías sin el portal.</li>
        </ul>
        <h3>Si modificas en SAP un pedido ya publicado</h3>
        <p>
          En el siguiente repaso el portal detecta el cambio, marca el pedido como <Badge tone="info">Modificado</Badge>, lo vuelve a poner{' '}
          <Badge tone="warn">Pendiente de aceptar</Badge> y avisa al proveedor para que lo revise y lo acepte de nuevo. No tienes que hacer
          nada más.
        </p>
      </Section>

      <Section id="proveedores" n={9} title="Dar de alta proveedores y usuarios">
        <p>Un proveedor solo ve el portal si está dado de alta y tiene al menos un usuario. Son dos pasos.</p>
        <h3>Paso 1 · Dar de alta el proveedor</h3>
        <ol className="pt-manual-steps">
          <li>
            Ve a <Link to="/proveedores">Proveedores</Link> y pulsa <strong>Dar de alta proveedor</strong>.
          </li>
          <li>Búscalo en SAP por nombre o código de interlocutor y elígelo.</li>
          <li>
            Opcional: escribe el correo donde quiere recibir los avisos. Si lo dejas vacío se usa el de su ficha de SAP.
          </li>
          <li>
            Pulsa <strong>Dar de alta</strong>. Desde ese momento sus pedidos abiertos aparecen en la cartera.
          </li>
        </ol>
        <p className="subtle">
          La primera vez que se da de alta un proveedor, sus pedidos que ya estaban abiertos se cargan sin mandarle un correo por cada uno.
        </p>
        <h3>Paso 2 · Darle un usuario</h3>
        <ol className="pt-manual-steps">
          <li>
            Ve a <Link to="/usuarios">Usuarios</Link> y pulsa <strong>Nuevo usuario</strong>.
          </li>
          <li>Escribe su correo y su nombre, elige el perfil «Proveedor» y el proveedor al que pertenece.</li>
          <li>
            Pulsa <strong>Crear usuario</strong>. Le llega la invitación para crear su contraseña. Mientras no la cree verás{' '}
            <Badge tone="warn">Invitación pendiente</Badge>; si la pierde, pulsa <strong>Reenviar invitación</strong>.
          </li>
        </ol>
        <p className="subtle">
          Un proveedor puede tener varios usuarios (por ejemplo, comercial y oficina técnica). El técnico de compras crea usuarios de
          proveedores; los usuarios del personal de {client} los gestiona el administrador.
        </p>
        <h3>Otras cosas desde la ficha del proveedor</h3>
        <ul>
          <li>
            <strong>Quitarle el acceso</strong>: desmarca «Acceso al portal activo». Sus usuarios dejan de entrar en el momento.
          </li>
          <li>
            <strong>Fijar su valoración al 100 %</strong>: para proveedores que no se quieren evaluar (ver el punto 10).
          </li>
          <li>
            <strong>Subir su logo</strong>: se ve en su portal y en el ranking.
          </li>
        </ul>
      </Section>

      <Section id="evaluacion" n={10} title="Evaluación de proveedores">
        <p>
          El portal puntúa a cada proveedor de 0 a 100 con su actividad de los últimos meses (6 por defecto) y los ordena en un ranking. La
          nota se recalcula sola cada día y se graba también en la ficha del proveedor en SAP.
        </p>
        <table className="table pt-manual-table">
          <thead>
            <tr>
              <th>Indicador</th>
              <th>Qué mide</th>
              <th className="num">Peso</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Calidad</td>
              <td>Líneas recibidas sin devolución sobre el total de líneas recibidas.</td>
              <td className="num">35 %</td>
            </tr>
            <tr>
              <td>Puntualidad</td>
              <td>Líneas recibidas en la fecha comprometida o antes.</td>
              <td className="num">35 %</td>
            </tr>
            <tr>
              <td>Disponibilidad</td>
              <td>Pedidos aceptados sobre pedidos respondidos (aceptados + rechazados).</td>
              <td className="num">15 %</td>
            </tr>
            <tr>
              <td>Agilidad</td>
              <td>Días que tarda en responder a un pedido: 100 si responde en un día o menos, 0 a partir de 7 días.</td>
              <td className="num">15 %</td>
            </tr>
          </tbody>
        </table>
        <ul>
          <li>
            En <Link to="/evaluacion">Evaluación</Link> pulsa <strong>Detalle</strong> en un proveedor para ver de dónde sale cada número.
          </li>
          <li>Si un indicador no tiene datos (por ejemplo, nada recibido en el periodo), no cuenta y su peso se reparte entre los demás.</li>
          <li>
            <strong>Recalcular ahora</strong> la actualiza en el momento, sin esperar al cálculo diario.
          </li>
          <li>Cada proveedor ve su nota y su puesto, pero no los nombres de los demás.</li>
        </ul>
      </Section>

      <Section id="avisos" n={11} title="Avisos por correo">
        <p>El portal manda los correos solo, desde noreply@ecinsa.com. Quién recibe cada uno:</p>
        <table className="table pt-manual-table">
          <thead>
            <tr>
              <th>Aviso</th>
              <th>Lo recibe</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Pedido nuevo pendiente de aceptar</td>
              <td>Proveedor</td>
            </tr>
            <tr>
              <td>Recordatorio de pedido sin aceptar (a los 3 y 5 días)</td>
              <td>Proveedor</td>
            </tr>
            <tr>
              <td>Pedido aceptado que se ha modificado en SAP</td>
              <td>Proveedor</td>
            </tr>
            <tr>
              <td>Resultado de la validación de una fecha o un precio</td>
              <td>Proveedor</td>
            </tr>
            <tr>
              <td>Cartera semanal (los lunes a las 7:00)</td>
              <td>Proveedor</td>
            </tr>
            <tr>
              <td>Pedido rechazado por el proveedor</td>
              <td>Compras</td>
            </tr>
            <tr>
              <td>Fecha fuera de tolerancia</td>
              <td>Compras</td>
            </tr>
            <tr>
              <td>Precio fuera de tolerancia</td>
              <td>Compras</td>
            </tr>
          </tbody>
        </table>
        <p>
          En <Link to="/avisos">Avisos</Link> ves el último repaso de SAP (pedidos publicados, modificados, recordatorios…) y los últimos
          100 correos con su estado: <Badge tone="ok">Enviado</Badge>, <Badge tone="warn">En cola</Badge> o{' '}
          <Badge tone="bad">Fallido</Badge>. Si un proveedor dice que no le ha llegado nada, mira aquí primero.
        </p>
      </Section>

      <Section id="configuracion" n={12} title="Configuración (administrador)">
        <p>
          Solo el administrador del portal ve <strong>Configuración</strong>. Allí se cambian, sin tocar SAP:
        </p>
        <ul>
          <li>
            <strong>Tolerancias</strong>: de precio (ahora ± {pct} %) y de fecha (ahora ± {days} días).
          </li>
          <li>
            <strong>Evaluación</strong>: los meses que se tienen en cuenta y el peso de cada indicador.
          </li>
          <li>
            <strong>Avisos</strong>: qué correos se envían, los días de los recordatorios, el día y la hora de la cartera semanal y los
            correos de compras que reciben los avisos (si se deja vacío, los usuarios con perfil de compras).
          </li>
        </ul>
      </Section>

      <Section id="dudas" n={13} title="Preguntas frecuentes">
        <dl className="pt-manual-faq">
          <dt>He creado un pedido en SAP y no aparece en el portal.</dt>
          <dd>
            Espera hasta 5 minutos o pulsa <strong>Repasar SAP ahora</strong>. Comprueba también que el proveedor esté dado de alta y con
            acceso activo en Proveedores: solo se ven los pedidos de esos proveedores.
          </dd>
          <dt>El proveedor dice que no le llegan los correos.</dt>
          <dd>
            Mira el correo de avisos en su ficha de Proveedores (o en su ficha de SAP) y busca el envío en Avisos. Que revise su correo no
            deseado.
          </dd>
          <dt>El portal dice que una línea no se puede cambiar porque tiene entregas parciales.</dt>
          <dd>
            SAP no deja tocar una línea que ya tiene una entrega parcial. Gestiona esa línea directamente en SAP.
          </dd>
          <dt>El albarán en PDF no se ha guardado.</dt>
          <dd>
            Los precios sí se graban; el PDF va a los anexos del pedido en SAP y necesita que SAP tenga configurada la carpeta de anexos. Si
            falla, avisa al administrador.
          </dd>
          <dt>El ojo del plano dice que no hay plano.</dt>
          <dd>Esa versión del plano todavía no está en el repositorio de planos del portal. Avisa a oficina técnica o al administrador.</dd>
          <dt>Un usuario se ha ido de la empresa.</dt>
          <dd>En Usuarios, edítalo y desactívalo: deja de poder entrar en el momento.</dd>
          <dt>¿Quién ha aceptado o cambiado algo?</dt>
          <dd>Abre el pedido: el historial dice quién hizo cada cosa y cuándo.</dd>
        </dl>
      </Section>
    </div>
  );
}

/**
 * El camino de un pedido dibujado. Las cajas resaltadas son los pasos en los
 * que interviene el personal del cliente; las demás las hace el proveedor o el
 * portal solo.
 */
function OrderFlow({ pct, days }: { pct: number; days: number }): JSX.Element {
  const W = 168;
  const H = 64;
  const cols = [24 + W / 2, 24 + W * 1.5 + 32, 24 + W * 2.5 + 64, 24 + W * 3.5 + 96];
  const rowA = 70;
  const rowB = 210;
  const ink = 'var(--ink)';
  const quiet = 'var(--ink-3)';
  const edge = 'var(--ink-3)';
  const box = (cx: number, cy: number, staff: boolean): JSX.Element => (
    <rect
      x={cx - W / 2}
      y={cy - H / 2}
      width={W}
      height={H}
      rx="8"
      fill={staff ? 'var(--accent-soft)' : 'var(--surface)'}
      stroke={staff ? 'var(--accent)' : 'var(--line)'}
      strokeWidth={staff ? 2 : 1.25}
    />
  );
  const label = (cx: number, cy: number, who: string, what: string, what2?: string): JSX.Element => (
    <>
      <text x={cx} y={cy - 12} textAnchor="middle" fontSize="11" fill={quiet}>
        {who}
      </text>
      <text x={cx} y={cy + 6} textAnchor="middle" fontSize="13" fontWeight="600" fill={ink}>
        {what}
      </text>
      {what2 ? (
        <text x={cx} y={cy + 22} textAnchor="middle" fontSize="13" fontWeight="600" fill={ink}>
          {what2}
        </text>
      ) : null}
    </>
  );
  const [c1, c2, c3, c4] = cols as [number, number, number, number];
  return (
    <svg className="pt-manual-flow" viewBox="0 0 800 300" role="img" aria-label="Recorrido de un pedido en el portal">
      <defs>
        <marker id="manual-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0 0L10 5L0 10z" fill={edge} />
        </marker>
      </defs>
      <g fill="none" stroke={edge} strokeWidth="1.25">
        <path d={`M${c1 + W / 2} ${rowA}H${c2 - W / 2}`} markerEnd="url(#manual-arrow)" />
        <path d={`M${c2 + W / 2} ${rowA}H${c3 - W / 2}`} markerEnd="url(#manual-arrow)" />
        <path d={`M${c3 + W / 2} ${rowA}H${c4 - W / 2}`} markerEnd="url(#manual-arrow)" />
        <path d={`M${c4} ${rowA + H / 2}V${rowB - H / 2}`} markerEnd="url(#manual-arrow)" />
        <path d={`M${c4 - W / 2} ${rowB}H${c3 + W / 2}`} markerEnd="url(#manual-arrow)" />
        <path d={`M${c3 - W / 2} ${rowB}H${c2 + W / 2}`} markerEnd="url(#manual-arrow)" />
        <path d={`M${c2 - W / 2} ${rowB}H${c1 + W / 2}`} markerEnd="url(#manual-arrow)" />
      </g>
      {box(c1, rowA, true)}
      {label(c1, rowA, 'Compras', 'Crea el pedido', 'en SAP')}
      {box(c2, rowA, false)}
      {label(c2, rowA, 'Portal · cada 5 min', 'Lo publica y', 'avisa al proveedor')}
      {box(c3, rowA, false)}
      {label(c3, rowA, 'Proveedor', 'Acepta', 'o rechaza')}
      {box(c4, rowA, false)}
      {label(c4, rowA, 'Proveedor', 'Propone fechas', `(± ${days} días)`)}
      {box(c4, rowB, false)}
      {label(c4, rowB, 'Proveedor', 'Comunica albarán', `y precios (± ${pct} %)`)}
      {box(c3, rowB, true)}
      {label(c3, rowB, 'Compras · si se sale', 'Valida fechas', 'y precios')}
      {box(c2, rowB, true)}
      {label(c2, rowB, 'Recepción', 'Confirma llegada', 'en playa')}
      {box(c1, rowB, false)}
      {label(c1, rowB, 'Portal', 'Luz verde', 'para la entrada')}
      <text x="24" y="290" fontSize="11" fill={quiet}>
        Resaltado: lo que hace el personal de la empresa. Lo demás lo hace el proveedor o el portal solo.
      </text>
    </svg>
  );
}
